import React, { useState } from 'react'
import Select from 'react-select'
import { Link, useHistory } from 'react-router-dom'
import { MagentoProductPrices, ProductData, ConfigurableVariant } from 'lib/types'
import { PRODUCT_TYPENAME } from 'lib/core/resources/constants'
import { useCart } from 'lib/core/hooks'
import { Breadcrumbs, MetaData } from 'lib/shared'
import { Button, Form, MediaPlaceholder, Toast } from 'lib/ui'
import {
  ProductCard,
  ProductNotification,
  ProductPrice,
  CustomOptions,
  CustomOptionsEvent,
  getCustomizableOptions,
  ConfigurableOptions,
  useProductStatus,
  useComparisonContext
} from 'lib/modules/catalog'

import { Container, Grid, ProductList } from './styled'
import qs from 'qs'

interface Props {
  product: ProductData
}

export const ProductScene = ({ product }: Props) => {
  const {
    name,
    image,
    sku,
    price,
    upsellProducts,
    relatedProducts,
    options,
    typeName
  } = product
  const history = useHistory()
  const [qty, setQty] = useState(1)
  const { addSimpleProduct, addConfigurableProduct } = useCart()
  const { isAddingToCart } = useProductStatus(product)
  const { addProductToCompare } = useComparisonContext()
  const [variant, setVariant] = useState<ProductData | undefined>(undefined)
  const [renderPrice, setRenderPrice] = useState(price)
  const [renderPriceType, setRenderPriceType] = useState(typeName)
  const [selectedRelated, setSelectedRelated] = useState<Record<string, boolean>>({})
  const [customOptions, setCustomOptions] = useState<CustomOptionsEvent | undefined>()
  const queryString = qs.parse(history.location.search.substring(1))
  const isSimpleProduct = typeName === PRODUCT_TYPENAME.SIMPLE // TODO: add to ProductData
  const isConfigurableProduct = typeName === PRODUCT_TYPENAME.CONFIGURABLE // TODO: add to ProductData
  const customOptionsErrors = Boolean(Object.keys(customOptions?.errors ?? {}).length)

  const addCurrentProductToCompare = (e: any) => {
    e.preventDefault()
    const added = sku && addProductToCompare(sku)
    if (!added) {
      Toast.toastFailure({ messageId: 'comparison.max' })
    } else {
      history.push('/product-comparison')
    }
  }

  const getRelatedProducts = () => {
    const products = relatedProducts
      ?.filter(product => selectedRelated[product.sku])
      .map(({ sku }) => ({
        quantity: qty,
        sku
      })) ?? []

    return products
  }

  function handleQtyChange (qtyOption: any) {
    setQty(parseInt(qtyOption.value))
  }

  function handlePriceChange (price: MagentoProductPrices) {
    setRenderPrice(price)
    setRenderPriceType(PRODUCT_TYPENAME.SIMPLE)
  }

  function handleConfigurableChange (variant?: ConfigurableVariant) {
    const product = variant?.product ?? undefined
    setVariant(product)
  }

  function handleAddConfigurableToCart () {
    const configurableSku = sku
    if (!variant) {
      return
    }

    const cartItems = [{
      configurableSku,
      quantity: qty,
      sku: variant.sku,
      customizableOptions: getCustomizableOptions(customOptions)
    }]

    addConfigurableProduct({
      configurableSku,
      cartItems,
      simpleProductsItems: getRelatedProducts()
    })
  }

  function handleAddSimpleToCart () {
    const cartItems = [
      {
        quantity: qty,
        sku: sku,
        customizableOptions: getCustomizableOptions(customOptions)
      },
      ...getRelatedProducts()
    ]

    addSimpleProduct({
      sku,
      cartItems
    })
  }

  return (
    <Container>
      <MetaData
        type='PDP'
      />
      <ProductNotification
        showToasts
        sku={sku}
        productName={name}
        productImage={variant?.image.small.url ?? image.default.url}
      />

      <Breadcrumbs />

      <Grid>
        <div>
          <MediaPlaceholder
            imageUrl={image.default.url}
          />
        </div>
        <div>
          <h1>{name}</h1>

          <p><i>sku: {sku}</i></p>

          <p>
            <strong>
              <ProductPrice
                extraPrice={customOptions?.priceDiff}
                price={renderPrice}
                type={renderPriceType}
              />
            </strong>
          </p>

          <ConfigurableOptions
            sku={sku}
            basePrice={renderPrice}
            onPriceChange={handlePriceChange}
            onChange={handleConfigurableChange}
            defaultVariant={queryString.sku?.toString()}
            reloadOptions
          />

          <CustomOptions
            options={options}
            onChange={setCustomOptions}
          />

          <Form.ReactSelectTheme.Wrapper>
            <Select
              classNamePrefix={Form.ReactSelectTheme.classNamePrefix}
              options={quantityOptions}
              onChange={handleQtyChange}
              defaultValue={quantityOptions[0]}
            />
          </Form.ReactSelectTheme.Wrapper>

          {relatedProducts?.map(product => {
            return (
              <p key={product.id}>
                <label>
                  <input
                    type='checkbox'
                    checked={Boolean(selectedRelated[product.sku])}
                    disabled={!product.isInStock}
                    onChange={e => {
                      setSelectedRelated({
                        ...selectedRelated,
                        [product.sku]: e.target.checked
                      })
                    }}
                  />
                  {` ${product.name} + `}
                  <strong>
                    <ProductPrice
                      price={product.price}
                      quantity={qty}
                    />
                  </strong>
                </label>
              </p>
            )
          })}

          <p>
            {isSimpleProduct && (
              <Button
                isLoading={isAddingToCart}
                isDisabled={customOptionsErrors}
                onClick={handleAddSimpleToCart}
                leftAdHoc={(
                  <ProductPrice
                    extraPrice={customOptions?.priceDiff}
                    price={renderPrice}
                    quantity={qty}
                    renderOldPrice={false}
                  />
                )}
              >
                Add To Cart
              </Button>
            )}

            {isConfigurableProduct && (
              <Button
                isLoading={isAddingToCart}
                isDisabled={!variant || customOptionsErrors}
                onClick={handleAddConfigurableToCart}
                leftAdHoc={(
                  <ProductPrice
                    extraPrice={customOptions?.priceDiff}
                    price={renderPrice}
                    quantity={qty}
                    renderOldPrice={false}
                  />
                )}
              >
                Add To Cart
              </Button>
            )}
          </p>
          <Link to='/product-comparison' onClick={addCurrentProductToCompare}>Compare</Link>
        </div>
      </Grid>
      {upsellProducts && (
        <ProductList>
          <div>
            {upsellProducts.map(product => {
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  listPage='Upsell Products'
                />
              )
            })}
          </div>
        </ProductList>
      )}
    </Container>
  )
}

const quantityOptions = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '10', label: '10' }
]
